import type { Actor } from '@hrms/shared'
import { type Driver, driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useCurrentUser } from '../auth/AuthProvider.tsx'
import { readJson, readStorage, writeJson } from '../lib/storage.ts'
import {
  getTour,
  isOnScreen,
  listTours,
  type TourDefinition,
  type TourStep,
  tourAvailable,
  waitForElement,
} from './registry.ts'
import './index.ts'

const SEEN_PREFIX = 'hrms.tours.seen'
/** Set to "1" (e2e tests, kiosks) to never auto-start tours. */
export const TOURS_DISABLED_KEY = 'hrms.tours.disabled'

const seenKey = (userId: string) => `${SEEN_PREFIX}:${userId}`

function seenTours(userId: string): string[] {
  const value = readJson<unknown>(seenKey(userId), [])
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

function markSeen(userId: string, id: string) {
  const seen = new Set(seenTours(userId))
  seen.add(id)
  writeJson(seenKey(userId), [...seen])
}

/** One tour at a time across the app. */
let active: Driver | null = null
let endActive: (() => void) | null = null
/** A tour was asked to start and is waiting for its page to render. */
let pending = false
const isActive = () => pending || active !== null

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Drives a tour step by step. Each step runs its `prepare`, then waits briefly for its element;
 * steps whose element never shows up (empty list, permissions, small screens) are skipped in the
 * direction of travel.
 */
async function run(tour: TourDefinition, user: Actor) {
  const steps: TourStep[] = typeof tour.steps === 'function' ? tour.steps(user) : tour.steps
  if (steps.length === 0) return
  let moving = false
  let finished = false
  // "Once per person" means once shown: a reload mid-tour doesn't start it again.
  markSeen(user.id, tour.id)

  const finish = () => {
    if (finished) return
    finished = true
    if (active === d) {
      active = null
      endActive = null
    }
    tour.onExit?.()
  }
  const end = () => {
    d.destroy()
    finish()
  }

  const d = driver({
    steps: steps.map(({ prepare: _p, ...s }) => s),
    showProgress: steps.length > 1,
    progressText: '{{current}} of {{total}}',
    popoverClass: 'hrms-tour',
    overlayColor: 'rgb(10, 14, 22)',
    overlayOpacity: 0.5,
    stagePadding: 6,
    stageRadius: 8,
    smoothScroll: !reducedMotion(),
    animate: !reducedMotion(),
    allowKeyboardControl: true,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    onNextClick: () => void go(1),
    onPrevClick: () => void go(-1),
    // Close button, Escape and overlay clicks all land here.
    onDestroyStarted: end,
    onDestroyed: finish,
  })
  endActive?.()
  active = d
  endActive = end

  /** First showable step from `from` going `dir`, or -1. */
  async function find(from: number, dir: 1 | -1): Promise<number> {
    for (let i = from; i >= 0 && i < steps.length; i += dir) {
      const step = steps[i]
      if (!step) continue
      try {
        await step.prepare?.()
      } catch {
        continue
      }
      if (!step.element) return i
      const el = await waitForElement(step.element)
      if (el) {
        // Inside a sideways-scrolling table: bring it into view (driver only scrolls vertically).
        const r = el.getBoundingClientRect()
        if (r.left < 0 || r.right > window.innerWidth)
          el.scrollIntoView({ block: 'nearest', inline: 'center' })
        return i
      }
    }
    return -1
  }

  async function go(dir: 1 | -1) {
    if (moving || active !== d) return
    moving = true
    try {
      const next = await find((d.getActiveIndex() ?? 0) + dir, dir)
      if (active !== d) return
      if (next < 0) {
        if (dir === 1) end()
        return
      }
      d.moveTo(next)
    } finally {
      moving = false
    }
  }

  const first = await find(0, 1)
  if (active !== d) return
  if (first < 0) {
    finish()
    return
  }
  d.drive(first)
}

export function useTour() {
  const user = useCurrentUser()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const available = useCallback(() => listTours().filter((t) => tourAvailable(t, user)), [user])

  const start = useCallback(
    (id: string) => {
      const tour = getTour(id)
      if (!tour || !tourAvailable(tour, user)) return false
      endActive?.()
      const move = !!tour.path && pathname !== tour.path
      if (move && tour.path) navigate(tour.path)
      pending = true
      // Let the page render (and the menu close) before measuring.
      window.setTimeout(
        () => {
          pending = false
          void run(tour, user)
        },
        move ? 350 : 50,
      )
      return true
    },
    [user, navigate, pathname],
  )

  const hasSeen = useCallback((id: string) => seenTours(user.id).includes(id), [user.id])

  return { start, available, hasSeen }
}

/**
 * Auto-runs each tour once per person: the shell intro first, then a page's own tour the first
 * time they land on that page. Never over an open dialog or another tour.
 */
export function useAutoTours() {
  const { start, available, hasSeen } = useTour()
  const { pathname, search } = useLocation()
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check when the URL changes (a closed drawer)
  useEffect(() => {
    if (readStorage(TOURS_DISABLED_KEY) === '1') return
    const timer = window.setTimeout(() => {
      if (isActive()) return
      if (isOnScreen(document.querySelector('[role="dialog"]'))) return
      const tours = available()
      const intro = tours.find((t) => t.autoStart && !t.path && !hasSeen(t.id))
      const next = intro ?? tours.find((t) => t.autoStart && t.path === pathname && !hasSeen(t.id))
      if (next) start(next.id)
    }, 900)
    return () => window.clearTimeout(timer)
  }, [available, hasSeen, start, pathname, search])
}
